import AccountBoxIcon from "@mui/icons-material/AccountBox";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import SettingsScreenViewModel, {
  DetachRequestItem,
} from "src/app/screens/settings/settings.viewmodel";
import styles from "src/app/styles/screens/settings.style";

const SettingsScreenView: React.FC = () => {
  const {
    selectedTab,
    onTabChanged,
    role,
    isCheckingDetachAvailability,
    isDetachAvailable,
    isRequestingDetach,
    onRequestDetachPressed,
    detachRequests,
    isLoadingDetachRequests,
    processingDetachRequestId,
    onDetachPressed,
    onDismissPressed,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    separatePagesPreference,
    onSeparatePagesPreferenceChanged,
    shareWithChildrenPreference,
    onShareWithChildrenPreferenceChanged,
  } = SettingsScreenViewModel();

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView
        selectedId="Settings"
        separateSites={separatePagesPreference}
      />

      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Settings
            </Typography>
          </Grid>

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              <Tabs
                value={selectedTab}
                onChange={onTabChanged}
                variant="fullWidth"
                scrollButtons="auto"
              >
                <Tab
                  icon={<TuneIcon />}
                  iconPosition="start"
                  label="Preferences"
                />
                <Tab
                  icon={<AccountBoxIcon />}
                  iconPosition="start"
                  label="Account"
                />
              </Tabs>

              <Divider />

              {selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  <Paper elevation={0} sx={styles.sectionCard}>
                    <Typography sx={styles.sectionDescription}>
                      Customize your app experience and set your preferences.
                    </Typography>
                    <Box sx={styles.actionRow}>
                      <Checkbox
                        color="primary"
                        checked={separatePagesPreference}
                        onChange={onSeparatePagesPreferenceChanged}
                      />
                      <Typography component="span" sx={styles.preferenceText}>
                        Separate W.A.R.P.A. and P.O.D. into two web pages
                      </Typography>
                    </Box>
                    {role === "Parent" && (
                      <Box sx={styles.actionRow}>
                        <Checkbox
                          color="primary"
                          checked={shareWithChildrenPreference}
                          onChange={onShareWithChildrenPreferenceChanged}
                        />
                        <Typography component="span" sx={styles.preferenceText}>
                          Share Cloudie and Files with children
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {selectedTab === 1 && ["Admin", "Model"].includes(role) && (
                <Box sx={styles.selectedTab}>
                  {role === "Model" && (
                    <Paper elevation={0} sx={styles.sectionCard}>
                      <Typography sx={styles.sectionTitle}>
                        Detach From Parents
                      </Typography>

                      <Typography sx={styles.sectionDescription}>
                        You can request to be detached from your parent accounts
                        if this option is available to you.
                      </Typography>

                      {isCheckingDetachAvailability ? (
                        <Box sx={styles.loaderContainer}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : (
                        <Box sx={styles.actionRow}>
                          <Button
                            variant="contained"
                            disabled={!isDetachAvailable}
                            loading={isRequestingDetach}
                            onClick={onRequestDetachPressed}
                          >
                            Request Detach From Parents
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  )}

                  {role === "Admin" && (
                    <Paper elevation={0} sx={styles.sectionCard}>
                      <Typography sx={styles.sectionTitle}>
                        Detach Requests
                      </Typography>

                      <Typography sx={styles.sectionDescription}>
                        Review detach requests and eligible users.
                      </Typography>

                      {isLoadingDetachRequests ? (
                        <Box sx={styles.loaderContainer}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : detachRequests.length === 0 ? (
                        <Typography sx={styles.emptyStateText}>
                          No detach requests available.
                        </Typography>
                      ) : (
                        <Box sx={styles.requestsList}>
                          {detachRequests.map((request: DetachRequestItem) => (
                            <Paper
                              key={request.model.id}
                              elevation={0}
                              sx={styles.requestCard}
                            >
                              <Typography sx={styles.requestTitle}>
                                {request.model.fullName}
                              </Typography>

                              <Typography sx={styles.requestMetaText}>
                                Birthdate: {request.model.birthdate}
                              </Typography>

                              <Typography sx={styles.requestMetaText}>
                                Parents:{" "}
                                {request.parents.length > 0
                                  ? request.parents
                                      .map((parent) => parent.fullName)
                                      .join(", ")
                                  : "No linked parents"}
                              </Typography>

                              <Box sx={styles.requestActionsContainer}>
                                <Button
                                  variant="contained"
                                  color="warning"
                                  loading={
                                    processingDetachRequestId ===
                                    request.model.id
                                  }
                                  disabled={
                                    processingDetachRequestId ===
                                    request.model.id
                                  }
                                  onClick={() => onDetachPressed(request)}
                                >
                                  Detach
                                </Button>

                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  loading={
                                    processingDetachRequestId ===
                                    request.model.id
                                  }
                                  disabled={
                                    processingDetachRequestId ===
                                    request.model.id
                                  }
                                  onClick={() => onDismissPressed(request)}
                                >
                                  Dismiss
                                </Button>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  )}

                  {role !== "Model" && role !== "Admin" && (
                    <Paper elevation={0} sx={styles.sectionCard}>
                      <Typography sx={styles.sectionTitle}>
                        Account Settings
                      </Typography>

                      <Typography sx={styles.emptyStateText}>
                        No settings are currently available for your account
                        type.
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default SettingsScreenView;
